// PHA-626: Clinic Settings editor for the onboarding script. Every section starts from
// Phanova's default wording, is always editable, and has an "Include this section" switch
// (on by default; an unchecked section is left out of the script). Edits and inclusion are
// saved together with "Save Script". The preview renders the script with the edits on a
// sample patient (or a real one, by MRN) before anything is saved.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  Flex,
  Heading,
  Text,
  TextArea,
  TextField,
} from '@radix-ui/themes';
import { CheckIcon, EyeOpenIcon, ResetIcon } from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import { clinicApi, patientApi } from '../../services/api';
import {
  ONBOARDING_SCRIPT_PLACEHOLDERS,
  ONBOARDING_SCRIPT_SECTION_IDS,
  OnboardingScriptSectionId,
  OnboardingScriptTemplate,
  OnboardingScriptTemplateEntry,
  PHANOVA_DEFAULT_SECTIONS,
  findUnknownPlaceholders,
  normalizeOnboardingScriptTemplate,
  splitScriptNotes,
} from '../../utils/onboardingScript';
import {
  PatientOnboardingScriptResult,
  PatientOnboardingScriptSource,
  buildPatientOnboardingScript,
} from '../../utils/patientOnboardingScript';

interface Props {
  /** organizations.onboarding_script_template as loaded with the clinic settings. */
  savedTemplate: unknown;
  clinicName: string;
  onSaved: (template: OnboardingScriptTemplate | null) => void;
}

interface SectionDraft {
  include: boolean;
  heading: string;
  body: string;
}

type Drafts = Record<OnboardingScriptSectionId, SectionDraft>;

type PreviewPatient = 'with_plan' | 'no_plan' | 'real';

const PREVIEW_OPTIONS: { value: PreviewPatient; label: string }[] = [
  { value: 'with_plan', label: 'Sample, with care plan' },
  { value: 'no_plan', label: 'Sample, no care plan' },
  { value: 'real', label: 'A real patient' },
];

const SAMPLE_PATIENT: PatientOnboardingScriptSource['patient'] = {
  first_name: 'Maria',
  apcm_tier: null,
  has_rpm_device: false,
  problems: [
    { problem: 'Hypertension' },
    { problem: 'Type 2 diabetes' },
    { problem: 'Hyperlipidemia' },
    { problem: 'Gastroesophageal reflux disease' },
  ],
  medications: [{ name: 'Lisinopril' }, { name: 'Metformin' }, { name: 'Atorvastatin' }],
};

const SAMPLE_CARE_PLAN: PatientOnboardingScriptSource['approvedDefinitions'] = [
  {
    disease: 'Hypertension',
    diseases: ['High blood pressure', 'Type 2 diabetes'],
    patient_friendly_plan: {
      goals: 'Keep your blood pressure below 130/80 and your blood sugar in a healthy range.',
      tasks: [
        { instruction: 'Check your blood pressure', how_often: 'every morning' },
        { instruction: 'Take your medicines as prescribed' },
      ],
    },
  },
];

function draftsFrom(saved: OnboardingScriptTemplate): Drafts {
  const out = {} as Drafts;
  for (const id of ONBOARDING_SCRIPT_SECTION_IDS) {
    const s = saved[id];
    out[id] = {
      include: s?.include !== false,
      heading: s?.heading ?? PHANOVA_DEFAULT_SECTIONS[id].heading,
      body: s?.body ?? PHANOVA_DEFAULT_SECTIONS[id].body,
    };
  }
  return out;
}

const isDefaultText = (id: OnboardingScriptSectionId, d: SectionDraft) =>
  d.heading.trim() === PHANOVA_DEFAULT_SECTIONS[id].heading && d.body.trim() === PHANOVA_DEFAULT_SECTIONS[id].body;

/**
 * Only what differs from Phanova's default is stored, so a section left at the default keeps
 * following it if the default wording changes later.
 */
function templateFrom(drafts: Drafts): OnboardingScriptTemplate {
  const out: OnboardingScriptTemplate = {};
  for (const id of ONBOARDING_SCRIPT_SECTION_IDS) {
    const d = drafts[id];
    const def = PHANOVA_DEFAULT_SECTIONS[id];
    const heading = d.heading.trim();
    const body = d.body.trim();
    const entry: OnboardingScriptTemplateEntry = {
      ...(heading && heading !== def.heading ? { heading } : {}),
      ...(body && body !== def.body ? { body } : {}),
      ...(d.include ? {} : { include: false }),
    };
    if (Object.keys(entry).length > 0) out[id] = entry;
  }
  return out;
}

const OnboardingScriptEditor: React.FC<Props> = ({ savedTemplate, clinicName, onSaved }) => {
  // Keyed on content, not identity: the clinic page reloads its settings after mount (e.g. when
  // the organization context settles), and a fresh-but-equal object must not wipe edits in progress.
  const savedKey = JSON.stringify(normalizeOnboardingScriptTemplate(savedTemplate));
  const saved = useMemo(() => JSON.parse(savedKey) as OnboardingScriptTemplate, [savedKey]);
  const [drafts, setDrafts] = useState<Drafts>(() => draftsFrom(saved));
  const [saving, setSaving] = useState(false);
  const [previewPatient, setPreviewPatient] = useState<PreviewPatient>('with_plan');
  const [previewMrn, setPreviewMrn] = useState('');
  const [realPatient, setRealPatient] = useState<{ patient: any; drafts: any[] } | null>(null);
  const [loadingPatient, setLoadingPatient] = useState(false);

  useEffect(() => {
    setDrafts(draftsFrom(saved));
  }, [saved]);

  const template = useMemo(() => templateFrom(drafts), [drafts]);
  // Compare in the same (default-stripped) form: a clinic whose saved text equals Phanova's
  // default (the Phanova clinic itself, on dev) must not open as "unsaved changes".
  const savedCanonical = useMemo(() => templateFrom(draftsFrom(saved)), [saved]);
  const dirty = JSON.stringify(template) !== JSON.stringify(savedCanonical);
  const unknownPlaceholders = useMemo(
    () =>
      Array.from(
        new Set(
          ONBOARDING_SCRIPT_SECTION_IDS.flatMap((id) =>
            drafts[id].include ? findUnknownPlaceholders(`${drafts[id].heading}\n${drafts[id].body}`) : [],
          ),
        ),
      ),
    [drafts],
  );

  const preview: PatientOnboardingScriptResult | null = useMemo(() => {
    if (previewPatient === 'real') {
      if (!realPatient) return null;
      return buildPatientOnboardingScript({
        patient: realPatient.patient,
        approvedDefinitions: realPatient.patient.care_plan_definitions,
        draftDefinitions: realPatient.drafts,
        template,
        clinicName: realPatient.patient.organization_name || clinicName,
      });
    }
    return buildPatientOnboardingScript({
      patient: SAMPLE_PATIENT,
      approvedDefinitions: previewPatient === 'with_plan' ? SAMPLE_CARE_PLAN : [],
      template,
      clinicName,
    });
  }, [previewPatient, realPatient, template, clinicName]);

  const update = (id: OnboardingScriptSectionId, patch: Partial<SectionDraft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const loadRealPatient = async () => {
    const mrn = previewMrn.trim();
    if (!mrn) return;
    setLoadingPatient(true);
    try {
      const p = await patientApi.get(mrn);
      if (!p?.patient_id) throw new Error('Patient not found');
      // As on the chart: with no approved plan, the draft is the patient's care plan.
      const drafts = (p.care_plan_definitions?.length ?? 0) > 0
        ? []
        : await patientApi.getCarePlanCandidates(String(p.patient_id), false).catch(() => []);
      setRealPatient({ patient: p, drafts: Array.isArray(drafts) ? drafts : [] });
    } catch (err: any) {
      setRealPatient(null);
      toast.error(err?.message || 'Could not load that patient');
    } finally {
      setLoadingPatient(false);
    }
  };

  const save = async () => {
    if (unknownPlaceholders.length > 0) {
      toast.error(`Unknown placeholder: {${unknownPlaceholders[0]}}`);
      return;
    }
    if (ONBOARDING_SCRIPT_SECTION_IDS.every((id) => !drafts[id].include)) {
      toast.error('Include at least one section.');
      return;
    }
    setSaving(true);
    try {
      const next = Object.keys(template).length > 0 ? template : null;
      const res = await clinicApi.updateOnboardingScript(next);
      toast.success('Onboarding script saved');
      onSaved(res?.onboarding_script_template ?? next);
    } catch (err: any) {
      console.error('Failed to save onboarding script:', err);
      toast.error(err?.message || 'Failed to save the onboarding script');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4" data-testid="onboarding-script-editor">
      <Box p="4">
        <Flex align="center" justify="between" gap="3" wrap="wrap">
          <Box>
            <Heading size="4">Onboarding Script</Heading>
            <Text size="2" color="gray">
              The script the care team reads to a patient from the chart's Actions menu. Edit any
              section's wording, and uncheck a section to leave it out.
            </Text>
          </Box>
          <Flex gap="2">
            <Button variant="soft" color="gray" onClick={() => setDrafts(draftsFrom(saved))} disabled={!dirty || saving}>
              <ResetIcon />
              Discard changes
            </Button>
            <Button variant="solid" onClick={save} disabled={!dirty || saving} data-testid="onboarding-script-save">
              <CheckIcon />
              {saving ? 'Saving...' : 'Save Script'}
            </Button>
          </Flex>
        </Flex>

        <Callout.Root color="gray" className="mt-3">
          <Callout.Text>
            Placeholders:{' '}
            {ONBOARDING_SCRIPT_PLACEHOLDERS.map((p, i) => (
              <span key={p.key}>
                {i > 0 && ' · '}
                <code>{`{${p.key}}`}</code> {p.description}
              </span>
            ))}
            . Start a line with "- " for a bullet.
          </Callout.Text>
        </Callout.Root>

        {unknownPlaceholders.length > 0 && (
          <Callout.Root color="red" className="mt-3">
            <Callout.Text>
              Unknown placeholder{unknownPlaceholders.length > 1 ? 's' : ''}:{' '}
              {unknownPlaceholders.map((k) => `{${k}}`).join(', ')}
            </Callout.Text>
          </Callout.Root>
        )}

        <Flex gap="4" className="mt-4" direction={{ initial: 'column', md: 'row' }}>
          <Flex direction="column" gap="3" style={{ flex: 1, minWidth: 0 }}>
            {ONBOARDING_SCRIPT_SECTION_IDS.map((id) => {
              const d = drafts[id];
              return (
                <Box key={id} className="border rounded p-3" style={d.include ? undefined : { opacity: 0.6 }}>
                  <Flex align="center" justify="between" gap="2">
                    <Text size="2" weight="medium">{PHANOVA_DEFAULT_SECTIONS[id].heading}</Text>
                    <Flex align="center" gap="2">
                      {!isDefaultText(id, d) && <Badge color="blue">Edited</Badge>}
                      <Text as="label" size="1" color="gray">
                        <Flex align="center" gap="1">
                          <Checkbox
                            checked={d.include}
                            onCheckedChange={(checked) => update(id, { include: checked === true })}
                            data-testid={`onboarding-script-include-${id}`}
                          />
                          Include this section
                        </Flex>
                      </Text>
                    </Flex>
                  </Flex>
                  <Flex direction="column" gap="2" className="mt-2">
                    <TextField.Root>
                      <TextField.Input
                        value={d.heading}
                        onChange={(e) => update(id, { heading: e.currentTarget.value })}
                        placeholder={PHANOVA_DEFAULT_SECTIONS[id].heading}
                        data-testid={`onboarding-script-heading-${id}`}
                      />
                    </TextField.Root>
                    <TextArea
                      rows={6}
                      value={d.body}
                      onChange={(e) => update(id, { body: e.currentTarget.value })}
                      data-testid={`onboarding-script-body-${id}`}
                    />
                    <Flex justify="end">
                      <Button
                        size="1"
                        variant="soft"
                        color="gray"
                        disabled={isDefaultText(id, d)}
                        onClick={() =>
                          update(id, {
                            heading: PHANOVA_DEFAULT_SECTIONS[id].heading,
                            body: PHANOVA_DEFAULT_SECTIONS[id].body,
                          })
                        }
                        data-testid={`onboarding-script-reset-${id}`}
                      >
                        <ResetIcon />
                        Reset to Phanova's default
                      </Button>
                    </Flex>
                  </Flex>
                </Box>
              );
            })}
          </Flex>

          <Box style={{ flex: 1, minWidth: 0 }}>
            <Flex align="center" gap="2" className="mb-2">
              <EyeOpenIcon />
              <Text size="2" weight="medium">Preview{dirty ? ' (unsaved changes)' : ''}</Text>
            </Flex>
            <Flex gap="1" wrap="wrap">
              {PREVIEW_OPTIONS.map((o) => (
                <Button
                  key={o.value}
                  size="1"
                  variant={previewPatient === o.value ? 'solid' : 'soft'}
                  onClick={() => setPreviewPatient(o.value)}
                >
                  {o.label}
                </Button>
              ))}
            </Flex>
            {previewPatient === 'real' && (
              <Flex gap="2" className="mt-2">
                <TextField.Root style={{ flex: 1 }}>
                  <TextField.Input
                    placeholder="Patient MRN"
                    value={previewMrn}
                    onChange={(e) => setPreviewMrn(e.currentTarget.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadRealPatient()}
                  />
                </TextField.Root>
                <Button variant="soft" onClick={loadRealPatient} disabled={loadingPatient || !previewMrn.trim()}>
                  {loadingPatient ? 'Loading...' : 'Load'}
                </Button>
              </Flex>
            )}
            <Box className="mt-3 border rounded p-3" style={{ maxHeight: '70vh', overflowY: 'auto' }} data-testid="onboarding-script-preview">
              {!preview ? (
                <Text size="2" color="gray">Enter a patient MRN to preview the script for them.</Text>
              ) : (
                <>
                  <Heading size="3" className="mb-2">{preview.script.title}</Heading>
                  {preview.notice && (
                    <Text as="p" size="1" color="orange" className="mb-2">{preview.notice}</Text>
                  )}
                  {preview.script.sections.map((s) => (
                    <Box key={s.id} className="mb-3">
                      <Text as="p" size="2" weight="bold" className="mb-1">{s.heading}</Text>
                      {s.paragraphs.map((p, i) => (
                        <Text key={i} as="p" size="2" className="mb-1">
                          {splitScriptNotes(p).map((seg, j) =>
                            seg.note ? (
                              <Text key={j} size="1" color="gray" className="italic">{seg.text}</Text>
                            ) : (
                              <React.Fragment key={j}>{seg.text}</React.Fragment>
                            ),
                          )}
                        </Text>
                      ))}
                      {s.bullets.length > 0 && (
                        <ul className="list-disc pl-5">
                          {s.bullets.map((b, i) => (
                            <li key={i}><Text size="2">{b}</Text></li>
                          ))}
                        </ul>
                      )}
                      {s.closing && <Text as="p" size="2" weight="bold" className="mt-1">{s.closing}</Text>}
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Box>
        </Flex>
      </Box>
    </Card>
  );
};

export default OnboardingScriptEditor;
